import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import moment from 'moment-timezone';
import { dateFilterTypesEnum } from '../../enums/date-filter-types.enum';
import { DateQueryModel } from '../../models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { DateFilterLocalizationsModel } from '../../models/date-filter-localizations.model';
import { DateFilterOptionModel } from '../../models/date-filter-option.model';
import { defaultDateFilterOptionsModel } from '../../models/default-date-filter-options.model';
import { dayNames } from '../../data/day-names';
import { shortDayNames } from '../../data/short-day-names';
import { minDayNames } from '../../data/min-day-names';
import { monthNames } from '../../data/month-names';
import { shortMonthNames } from '../../data/short-month-names';

export const dateFilterComponentText: any = {
    none: 'None',
    presetDateOptions: 'Preset date options',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    lastWeek: 'Last week',
    custom: 'Custom',
};

/** @deprecated */
@Component({
    selector: 'agpr-date-filter',
    templateUrl: './date-filter.component.html',
    styleUrls: ['./date-filter.component.scss'],
    standalone: false
})
export class DateFilterComponent implements OnInit {
    @Input() id: string;
    // Inputs passed to PrimeNG datepicker
    @Input() defaultDate: Date = null;
    @Input() selectionMode: string = 'single';
    @Input() inputStyleClass: string = null;
    @Input() name: string = null;
    @Input() placeholder: string = null;
    @Input() disabled: boolean = false;
    @Input() dateFormat: string = 'mm/dd/yy';
    @Input() inline: boolean = null;
    @Input() showOtherMonths: boolean = null;
    @Input() selectOtherMonths: boolean = null;
    @Input() showOnFocus: boolean = false;
    @Input() icon: string = null;
    @Input() showIcon: boolean = null;
    @Input() appendTo: any = 'body';
    @Input() shortYearCutoff: string = null;
    @Input() minDate: Date = null;
    @Input() maxDate: Date = null;
    @Input() disabledDates: Date[] = null;
    @Input() disabledDays: number[] = null;
    @Input() monthNavigator: boolean = null;
    @Input() yearNavigator: boolean = null;
    @Input() yearRange: string = null;
    @Input() showTime: boolean = null;
    @Input() hourFormat: string = '24';
    @Input() locale: any = null;
    @Input() timeOnly: boolean = null;
    @Input() dataType: string = 'date';
    @Input() required: boolean = null;
    @Input() tabindex: number = null;
    @Input() showSeconds: boolean = null;
    @Input() stepHour: number = null;
    @Input() stepMinute: number = null;
    @Input() stepSecond: number = null;
    /** @deprecated */
    @Input() utc: boolean = null;
    @Input() maxDateCount: number = null;
    @Input() showButtonBar: boolean = false;
    @Input() todayButtonStyleClass: string = 'ui-secondary-button';
    @Input() clearButtonStyleClass: string = 'ui-secondary-button';
    @Input() baseZIndex: number = 0;
    @Input() autoZIndex: boolean = true;
    @Input() panelStyleClass: string = null;
    @Input() keepInvalid: boolean = null;
    @Input() hideOnDateTimeSelect: boolean = null;
    // Makes the input box un-interactable
    @Input() readonlyInput: boolean = null;

    // Inputs passed to dropdown
    @Input() dropdownStyleClass: string | Object = null;

    // Inputs passed to date filter
    @Input() styleClass: string = null;

    // Outputs passed from datepicker
    @Output() select: EventEmitter<any> = new EventEmitter<any>();
    @Output() blur: EventEmitter<any> = new EventEmitter<any>();
    @Output() focus: EventEmitter<any> = new EventEmitter<any>();
    @Output() close: EventEmitter<any> = new EventEmitter<any>();
    @Output() input: EventEmitter<any> = new EventEmitter<any>();
    @Output() todayInput: EventEmitter<any> = new EventEmitter<any>();
    @Output() clearClick: EventEmitter<any> = new EventEmitter<any>();
    @Output() monthChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() yearChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() dateQueryChange: EventEmitter<DateQueryModel> = new EventEmitter<DateQueryModel>();

    today: Date = moment()
        .toDate();
    yesterday: Date = moment()
        .subtract(1, 'days')
        .toDate();
    oneWeekAgo: Date = moment()
        .subtract(7, 'days')
        .toDate();
    twoWeeksAgo: Date = moment()
        .subtract(14, 'days')
        .toDate();

    dates: Date | Date[];
    selectedDateQuery: DateQueryModel;
    previousDateQuery: DateQueryModel;
    /* eslint-disable @typescript-eslint/naming-convention */
    calendarIcon_sr = 'Date options';
    dateTimeFilter = 'Date Filter';

    defaultLocale = {
        firstDayOfWeek: 0,
        dayNames: dayNames,
        dayNamesShort: shortDayNames,
        dayNamesMin: minDayNames,
        monthNames: monthNames,
        monthNamesShort: shortMonthNames,
        today: 'Today',
        clear: 'Clear',
    };

    @ViewChild('calendar') calendar: DatePicker;
    @ViewChild('dropdown') dropdown: Select;

    @Input() options: DateFilterOptionModel[] = defaultDateFilterOptionsModel;

    @Input()
    set dateRange(dateQuery: DateQueryModel) {
        // We seem to come in with a null parameter, so let's return quickly if that is the case
        if (dateQuery === null) {
            this.dates = undefined;
            return;
        }

        const hasDateQuery: boolean = dateQuery !== null && dateQuery !== undefined;
        const hasSelectedDateQuery: boolean = this.selectedDateQuery !== null && this.selectedDateQuery !== undefined;
        const dateQueryChanged: boolean = hasSelectedDateQuery && !this.dateQueriesEquals(dateQuery, this.selectedDateQuery);
        const isRelativeDateQuery: boolean = hasDateQuery && dateQuery.type === DateQueryTypeEnum.RELATIVE;
        const selectedOption: DateFilterOptionModel = this.findMatchingOptionByDate(dateQuery);

        if (hasSelectedDateQuery && !dateQueryChanged) {
            return;
        }

        this.previousDateQuery = this.selectedDateQuery;
        this.selectedDateQuery = dateQuery;

        if (!hasDateQuery) {
            this.dates = undefined;
        } else if (isRelativeDateQuery) {
            const isSingleDay: boolean = this.isSingleDay(dateQuery);
            const isToday: boolean = dateQuery.relativeStartOffsetH === 24;
            let startDate: Date = moment().add(dateQuery.relativeStartOffsetH, 'hours').toDate();
            const endDate: Date = moment().add(dateQuery.relativeEndOffsetH, 'hours').toDate();

            if (isToday) {
                this.dates = new Date();
            } else if (isSingleDay) {
                startDate = moment(startDate).add(1, 'days').toDate();

                this.selectionMode = 'single';
                this.dates = startDate;
            } else {
                this.selectionMode = 'range';
                this.dates = [startDate, endDate];
            }
            this.changeDateWithPresetValue();

        } else if (dateQuery.absoluteRange) {
            this.selectionMode = 'range';
            this.dates = [dateQuery.absoluteRange.start, dateQuery.absoluteRange.end];
        } else {
            this.dates = undefined;
            this.changeDateWithPresetValue();
        }
    }

    @Input()
    set localization(localizations: DateFilterLocalizationsModel) {
        if (localizations) {
            if (localizations.filterTypes) {
                for (const filter of this.options) {
                    const filterName = localizations.filterTypes.get(filter.type);
                    if (filterName) {
                        filter.label = filterName;
                    }
                }
            }
            if (localizations.calendarIcon) {
                this.calendarIcon_sr = localizations.calendarIcon;
            }
            if (localizations.dateTimeFilter) {
                this.dateTimeFilter = localizations.dateTimeFilter;
            }
        }

    }


    constructor() {
        this.locale = this.locale || this.defaultLocale;
    }

    ngOnInit(): void {
        if (!this.selectedDateQuery || !this.selectedDateQuery.type) {
            this.selectedDateQuery = this.options[1].value;
        }
        this.changeDateWithPresetValue();
    }

    /**
     * Updates the date settings based on the selected option
    */
    onSelectedOptionChange(event): void {
        const selectedOption: DateFilterOptionModel = this.findMatchingOptionByDate(this.selectedDateQuery);
        const hasSelectedOption: boolean = selectedOption !== null && selectedOption !== undefined;
        const isEmptyDate: boolean = hasSelectedOption && selectedOption.type === dateFilterTypesEnum.none;
        const isCustomDate: boolean = hasSelectedOption && selectedOption.type === dateFilterTypesEnum.custom;

        if (isEmptyDate) {
            this.dates = null;
            this.dateQueryChange.emit(null);
            this.changeDateWithPresetValue();
        } else if (isCustomDate) {
            this.showCalendar();
            this.previousDateQuery = this.selectedDateQuery;
            this.selectedDateQuery = null;
        } else {
            this.changeDateWithPresetValue();
            this.dateQueryChange.emit(this.selectedDateQuery);
            this.dates = null;
        }
        this.dropdown.overlayVisible = false;
    }

    showCalendar() {
        // TODO : Should give focus to the calendar
        if (this.calendar) {
            this.calendar.selectionMode = 'range';
            setTimeout(() => {
                this.calendar.showOverlay();
            }, 1);
        }
    }

    /**
     * Emits the appropriate dateQuery if a user clicked a preset date
     */
    changeDateWithPresetValue(): void {
        this.dataType = 'string';
        let dateLabel = '';
        const selectedOption = this.options.find(option => {
            if (!option) {
                return false;
            }
            return this.dateQueriesEquals(option.value, this.selectedDateQuery);
        });
        if (selectedOption) {
            dateLabel = selectedOption.label;
        }

        // HACK: Without this the input stays empty if you change from 24 hours -> custom -> 24 hours, no explanation, I tried
        // many things before landing on this, including, this.dates = ..., timeout -> this.dates = ...,
        // request animation -> this.dates = ..., this.calendar.updateModel(...), timeout -> this.calendar.updateModel(...), etc
        // this is the only one that worked, sorry
        // @TODO: See if this is still a problem in newer versions of primeNg
        requestAnimationFrame(
            () => {
                if (this.calendar) {
                    this.calendar.inputfieldViewChild.nativeElement.value = dateLabel;
                }
            }
        );
    }

    /**
     * Passes the onSelect event from the PrimeNG calendar component
     * to this component's parent
     */
    emitOnSelect(event: any): void {
        this.select.emit(event);
    }

    /**
     * Passes the onBlur event from the PrimeNG calendar component
     * to this component's parent
     */
    emitOnBlur(event: Event): void {
        this.blur.emit(event);
    }

    /**
     * Passes the onFocus event from the PrimeNG calendar component
     * to this component's parent
     */
    emitOnFocus(event: Event): void {
        this.focus.emit(event);
    }

    // When the calendar is closed, we emit a dateQuery change if at least one date was selected
    // TODO : Ideally it should only fire if the dates have changed.  Currently it always fires.
    emitOnClose(event: Event): void {
        // The user must at least have selected SOMETHING
        if (this.calendar.value && this.calendar.value[0]) {
            const dateQuery = new DateQueryModel();
            // If the user only clicked one day and closed the calendar,
            // we'll set the endDate for him and consider it a one-day interval
            let endDate = moment(this.calendar.value[0]).add(1, 'days').toDate();
            if (this.calendar.value[1]) {
                endDate = this.calendar.value[1];
                // We want it to go from the start of the first day, to the last possible second of the end date
                endDate = moment(endDate).endOf('day').toDate();
            } else {
                this.selectionMode = 'single';
            }
            dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
            dateQuery.absoluteRange = {
                start: this.calendar.value[0],
                end: endDate
            };
            this.selectedDateQuery = dateQuery;
            this.dateQueryChange.emit(dateQuery);
        }
        this.close.emit(event);
    }

    /**
     * Passes the onTodayInput event from the PrimeNG calendar component
     * to this component's parent
     */
    emitOnTodayClick(event: Event): void {
        this.todayInput.emit(event);
    }

    /**
     * Passes the onClearClick event from the PrimeNG calendar component
     * to this component's parent
     */
    emitOnClearClick(event: Event): void {
        this.clearClick.emit(event);
    }

    /**
     * Passes the onMonthChange event from the PrimeNG calendar component
     * to this component's parent
     */
    emitOnMonthChange(event: any): void {
        this.monthChange.emit(event);
    }

    /**
     * Passes the onYearChange event from the PrimeNG calendar component
     * to this component's parent
     */
    emitOnYearChange(event: any): void {
        this.yearChange.emit(event);
    }

    /**
     * Finds the date filter option that matches the provided date query
    */
    private findMatchingOptionByDate(dateQuery: DateQueryModel): DateFilterOptionModel {
        let option: DateFilterOptionModel = null;
        const hasDateQuery: boolean = dateQuery !== null && dateQuery !== undefined;
        const isRelativeDateQuery: boolean = hasDateQuery && dateQuery.type === DateQueryTypeEnum.RELATIVE;
        const isAbsoluteDateQuery: boolean = hasDateQuery && dateQuery.type === DateQueryTypeEnum.ABSOLUTE;

        if (!hasDateQuery) {
            option = this.findMatchingOptionByType(dateFilterTypesEnum.none);
        } else if (isAbsoluteDateQuery) {
            option = this.findMatchingOptionByType(dateFilterTypesEnum.custom);
        } else if (isRelativeDateQuery) {

            this.options.find(currentOption => {

                let isMatch: boolean = false;
                const hasValue: boolean = currentOption.value !== null && currentOption.value !== undefined;
                const isRelative: boolean = hasValue && currentOption.value.type === DateQueryTypeEnum.RELATIVE;

                if (isRelative) {
                    const isMatchingStart: boolean = currentOption.value.relativeStartOffsetH === dateQuery.relativeStartOffsetH;
                    const isMatchingEnd: boolean = currentOption.value.relativeEndOffsetH === dateQuery.relativeEndOffsetH;

                    isMatch = isMatchingStart && isMatchingEnd;

                    return isMatch;
                }

            });

        } else {
            option = this.findMatchingOptionByType(dateFilterTypesEnum.none);
        }

        return option;
    }

    /**
     * Finds the date filter option that matches the provided date date filter type
    */
    private findMatchingOptionByType(type: dateFilterTypesEnum): DateFilterOptionModel {
        const option: DateFilterOptionModel = this.options.find(currentOption => currentOption.type === type);

        return option;
    }

    /**
     * Determines whether a date query with a relative date type
     * has a range of a single day
    */
    private isSingleDay(dateQuery: DateQueryModel): boolean {
        let isSingleDay: boolean = false;
        const hasDateQuery: boolean = dateQuery !== null && dateQuery !== undefined;
        let isRelativeDateQuery: boolean;

        if (hasDateQuery) {
            isRelativeDateQuery = dateQuery.type === DateQueryTypeEnum.RELATIVE;
            isSingleDay = isRelativeDateQuery && dateQuery.relativeEndOffsetH - dateQuery.relativeStartOffsetH <= 24;
        }

        return isSingleDay;
    }

    /**
     * Checks for equality of two date objects
     * @param objectA -  object to compare for equality
     * @param objectB - object to compare for equality
     *
     * @returns boolean
     */
    private dateQueriesEquals(objectA: DateQueryModel, objectB: DateQueryModel): boolean {

        if (objectA === objectB) {
            return true;
        }

        if (!objectA) {
            return false;
        }

        if (!objectB) {
            return false;
        }

        if (objectA.type !== objectB.type) {
            return false;
        }
        if (objectA.relativeStartOffsetH !== objectB.relativeStartOffsetH) {
            return false;
        }
        if (objectA.relativeEndOffsetH !== objectB.relativeEndOffsetH) {
            return false;
        }
        if (objectA.absoluteDate !== objectB.absoluteDate) {
            return false;
        }
        if (objectA.absoluteRange !== objectB.absoluteRange) {
            return false;
        }

        return true;
    }

}
